import { useCallback, useEffect, useRef } from 'react';
import { useTimer } from '@/hooks/useTimer';

/**
 * Cronómetro de sesión de estudio con guardado incremental.
 *
 * Misma lógica de pausa que `useTimer` (pestaña oculta + inactividad). Envía
 * *deltas* (segundos nuevos desde el último envío) a `sendDelta` cada 30s y al
 * desmontar, de modo que el tiempo cuenta aunque el alumno cierre sin enviar.
 * No persiste agregados por resultado: eso lo hace el `submit` de cada tipo.
 *
 * @param enabled    activa/desactiva el cronómetro
 * @param sendDelta  persiste un delta de segundos (no debe lanzar de forma crítica)
 * @returns  `time` — segundos locales acumulados de la sesión
 */
export function useStudyTimeTracker(
  enabled: boolean,
  sendDelta: (deltaSeconds: number) => Promise<void> | void
): { time: number } {
  const lastSentRef = useRef(0);
  const timeRef = useRef(0);

  // Ref para no recrear el efecto si el llamante pasa una función inline.
  const sendDeltaRef = useRef(sendDelta);
  sendDeltaRef.current = sendDelta;

  const flush = useCallback((totalSeconds: number) => {
    const delta = Math.round(totalSeconds - lastSentRef.current);
    if (delta < 1) return;
    lastSentRef.current = totalSeconds;
    try {
      void Promise.resolve(sendDeltaRef.current(delta)).catch(() => {
        /* sin bloqueo de lectura */
      });
    } catch {
      /* sin bloqueo de lectura */
    }
  }, []);

  const { time, start, stop } = useTimer({
    onUpdate: flush,
    autoSaveInterval: 30,
  });

  // Asignación directa en render: segura para refs, evita un tick de retraso.
  timeRef.current = time;

  useEffect(() => {
    if (!enabled) return;
    lastSentRef.current = 0;
    start();
    return () => {
      flush(timeRef.current);
      stop();
    };
  }, [enabled, start, stop, flush]);

  return { time };
}
